import {
  csrfTokenResponseSchema,
  loginRequestSchema,
  meResponseSchema,
  messageResponseSchema,
  passwordResetConfirmSchema,
  passwordResetRequestSchema,
  type LoginRequest,
  type MeResponse,
  type PasswordResetConfirm,
  type PasswordResetRequest,
} from '@console/types'
import { z } from 'zod'
import { apiGet, apiPost, ApiError } from '../api.js'

export async function fetchCsrfToken(): Promise<string> {
  const { csrfToken } = await apiGet('/auth/csrf', csrfTokenResponseSchema)
  return csrfToken
}

/**
 * `null` means "not signed in" — a normal, expected state, not a fetch
 * failure. Any other failure (the server unreachable, a 500) is left to
 * throw, so the caller can tell "you are logged out" apart from "something
 * is actually broken" instead of collapsing both into the same blank screen.
 */
export async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await apiGet('/auth/me', meResponseSchema)
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null
    throw error
  }
}

export async function login(credentials: LoginRequest): Promise<MeResponse> {
  loginRequestSchema.parse(credentials)
  const csrfToken = await fetchCsrfToken()
  return apiPost('/auth/login', credentials, csrfToken, meResponseSchema)
}

export async function logout(): Promise<void> {
  const csrfToken = await fetchCsrfToken()
  await apiPost('/auth/logout', undefined, csrfToken, z.void())
}

export async function requestPasswordReset(
  request: PasswordResetRequest,
): Promise<{ message: string }> {
  passwordResetRequestSchema.parse(request)
  const csrfToken = await fetchCsrfToken()
  return apiPost('/auth/password-reset/request', request, csrfToken, messageResponseSchema)
}

export async function confirmPasswordReset(
  request: PasswordResetConfirm,
): Promise<{ message: string }> {
  passwordResetConfirmSchema.parse(request)
  const csrfToken = await fetchCsrfToken()
  return apiPost('/auth/password-reset/confirm', request, csrfToken, messageResponseSchema)
}
